import * as Popover from "@radix-ui/react-popover";
import {
    type ChangeEvent,
    type PointerEvent as ReactPointerEvent,
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from "react";
import { cx } from "../../utils/cx";
import { SegmentControl } from "../SegmentControl";
import { Slider } from "../Slider";
import {
    type ColorFieldMode,
    type ColorStop,
    type ColorValue,
    type GradientKind,
    type SolidColorFormat,
    clamp,
    colorValueToCss,
    colorValueToLabel,
    colorValueToThemePair,
    createStopId,
    defaultGradient,
    defaultSolid,
    hexToHsl,
    hexToHsv,
    hexToRgb,
    hslToHex,
    hsvToHex,
    nextSolidColorFormat,
    normalizeHex,
    normalizeHex6,
    parseCssGradient,
    rgbToHex,
    seedGradientFromSolid,
    solidFromValue,
} from "./colorValue";
import styles from "./ColorField.module.scss";

export type ColorFieldVariant = "popover" | "accordion";

export type ColorFieldProps = {
    label: string;
    value: ColorValue;
    onValueChange: (value: ColorValue) => void;
    disabled?: boolean;
    description?: string;
    /** Restrict available modes (default both). */
    allowedModes?: ColorFieldMode[];
    /**
     * `popover` — floating editor (default).
     * `accordion` — inline expand under the trigger (e.g. Customize sidebar).
     */
    variant?: ColorFieldVariant;
    /** Controlled open state (useful for single-open accordion groups). */
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    className?: string;
};

const MODE_ITEMS = [
    { id: "solid", label: "Solid" },
    { id: "gradient", label: "Gradient" },
] as const;

const KIND_ITEMS = [
    { id: "linear", label: "Linear" },
    { id: "radial", label: "Radial" },
    { id: "conic", label: "Conic" },
] as const;

function Swatch({ css, className }: { css: string; className?: string }) {
    return (
        <span
            aria-hidden
            className={cx(styles.swatch, className)}
            style={{ background: css }}
        />
    );
}

function SolidPanel({
    hex,
    onHexChange,
}: {
    hex: string;
    onHexChange: (hex: string) => void;
}) {
    const hsv = useMemo(() => hexToHsv(hex), [hex]);
    const rgb = useMemo(() => hexToRgb(hex), [hex]);
    const hsl = useMemo(() => hexToHsl(hex), [hex]);
    const [format, setFormat] = useState<SolidColorFormat>("hex");
    const [hexDraft, setHexDraft] = useState(hex);
    const areaRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setHexDraft(hex);
    }, [hex]);

    const commitSv = useCallback(
        (clientX: number, clientY: number) => {
            const el = areaRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const s = clamp((clientX - rect.left) / rect.width, 0, 1);
            const v = clamp(1 - (clientY - rect.top) / rect.height, 0, 1);
            onHexChange(hsvToHex(hsv.h, s, v));
        },
        [hsv.h, onHexChange],
    );

    const onAreaPointer = useCallback(
        (e: ReactPointerEvent<HTMLDivElement>) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            commitSv(e.clientX, e.clientY);
        },
        [commitSv],
    );

    const onAreaMove = useCallback(
        (e: ReactPointerEvent<HTMLDivElement>) => {
            if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
            commitSv(e.clientX, e.clientY);
        },
        [commitSv],
    );

    const hueColor = hsvToHex(hsv.h, 1, 1);

    const cycleFormat = () => setFormat(prev => nextSolidColorFormat(prev));

    return (
        <div className={styles.solidPanel}>
            <div
                className={styles.svArea}
                onPointerDown={onAreaPointer}
                onPointerMove={onAreaMove}
                ref={areaRef}
                style={{ backgroundColor: hueColor }}
            >
                <span
                    className={styles.svThumb}
                    style={{
                        left: `${hsv.s * 100}%`,
                        top: `${(1 - hsv.v) * 100}%`,
                        background: hex,
                    }}
                />
            </div>

            <div className={styles.hueRow}>
                <Swatch className={styles.hueSwatch} css={hex} />
                <div className={styles.hueSlider}>
                    <Slider
                        aria-label="Hue"
                        fullWidth
                        max={360}
                        min={0}
                        onValueChange={v => {
                            const h = typeof v === "number" ? v : v[0];
                            onHexChange(hsvToHex(h, hsv.s, hsv.v));
                        }}
                        step={1}
                        value={Math.round(hsv.h)}
                    />
                </div>
            </div>

            <div className={styles.formatBlock}>
                <div className={styles.formatHeader}>
                    <span className={styles.fieldCaption}>
                        {format.toUpperCase()}
                    </span>
                    <button
                        aria-label={`Switch color format (current ${format.toUpperCase()})`}
                        className={styles.formatCycle}
                        onClick={cycleFormat}
                        title="Cycle HEX → RGB → HSL"
                        type="button"
                    >
                        <span aria-hidden className={styles.formatCycleIcon}>
                            ↕
                        </span>
                    </button>
                </div>

                {format === "hex" ? (
                    <input
                        aria-label="HEX color"
                        className={styles.textInput}
                        onBlur={() => {
                            // Allow #RGB → #RRGGBB only when the user finishes editing.
                            const next = normalizeHex(hexDraft);
                            if (next) onHexChange(next);
                            else setHexDraft(hex);
                        }}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setHexDraft(e.target.value);
                            // Live updates only for complete 6-digit hex (avoid #RGB expand mid-type).
                            const next = normalizeHex6(e.target.value);
                            if (next) onHexChange(next);
                        }}
                        spellCheck={false}
                        value={hexDraft}
                    />
                ) : null}

                {format === "rgb" ? (
                    <div className={styles.channelRow}>
                        {(
                            [
                                ["R", "r", rgb.r, 255],
                                ["G", "g", rgb.g, 255],
                                ["B", "b", rgb.b, 255],
                            ] as const
                        ).map(([label, key, val, max]) => (
                            <label className={styles.channel} key={key}>
                                <span className={styles.channelLabel}>
                                    {label}
                                </span>
                                <input
                                    className={styles.channelInput}
                                    max={max}
                                    min={0}
                                    onChange={(
                                        e: ChangeEvent<HTMLInputElement>,
                                    ) => {
                                        const n = Number(e.target.value);
                                        if (!Number.isFinite(n)) return;
                                        const next = {
                                            ...rgb,
                                            [key]: clamp(Math.round(n), 0, max),
                                        };
                                        onHexChange(
                                            rgbToHex(next.r, next.g, next.b),
                                        );
                                    }}
                                    type="number"
                                    value={val}
                                />
                            </label>
                        ))}
                    </div>
                ) : null}

                {format === "hsl" ? (
                    <div className={styles.channelRow}>
                        <label className={styles.channel}>
                            <span className={styles.channelLabel}>H</span>
                            <input
                                className={styles.channelInput}
                                max={360}
                                min={0}
                                onChange={(
                                    e: ChangeEvent<HTMLInputElement>,
                                ) => {
                                    const n = Number(e.target.value);
                                    if (!Number.isFinite(n)) return;
                                    onHexChange(
                                        hslToHex(
                                            clamp(n, 0, 360),
                                            hsl.s,
                                            hsl.l,
                                        ),
                                    );
                                }}
                                type="number"
                                value={Math.round(hsl.h)}
                            />
                        </label>
                        <label className={styles.channel}>
                            <span className={styles.channelLabel}>S</span>
                            <input
                                className={styles.channelInput}
                                max={100}
                                min={0}
                                onChange={(
                                    e: ChangeEvent<HTMLInputElement>,
                                ) => {
                                    const n = Number(e.target.value);
                                    if (!Number.isFinite(n)) return;
                                    onHexChange(
                                        hslToHex(
                                            hsl.h,
                                            clamp(n, 0, 100) / 100,
                                            hsl.l,
                                        ),
                                    );
                                }}
                                type="number"
                                value={Math.round(hsl.s * 100)}
                            />
                        </label>
                        <label className={styles.channel}>
                            <span className={styles.channelLabel}>L</span>
                            <input
                                className={styles.channelInput}
                                max={100}
                                min={0}
                                onChange={(
                                    e: ChangeEvent<HTMLInputElement>,
                                ) => {
                                    const n = Number(e.target.value);
                                    if (!Number.isFinite(n)) return;
                                    onHexChange(
                                        hslToHex(
                                            hsl.h,
                                            hsl.s,
                                            clamp(n, 0, 100) / 100,
                                        ),
                                    );
                                }}
                                type="number"
                                value={Math.round(hsl.l * 100)}
                            />
                        </label>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function GradientPanel({
    value,
    onChange,
}: {
    value: Extract<ColorValue, { mode: "gradient" }>;
    onChange: (value: Extract<ColorValue, { mode: "gradient" }>) => void;
}) {
    const [activeStopId, setActiveStopId] = useState(value.stops[0]?.id);
    const [cssDraft, setCssDraft] = useState(() => colorValueToCss(value));
    const [cssError, setCssError] = useState(false);

    useEffect(() => {
        setCssDraft(colorValueToCss(value));
        setCssError(false);
    }, [value]);

    const commitCssDraft = () => {
        const parsed = parseCssGradient(cssDraft);
        if (!parsed) {
            setCssError(true);
            setCssDraft(colorValueToCss(value));
            return;
        }
        setCssError(false);
        onChange(parsed);
        setActiveStopId(parsed.stops[0]?.id);
    };

    const activeStop =
        value.stops.find(s => s.id === activeStopId) || value.stops[0];

    const updateStop = (id: string, patch: Partial<ColorStop>) => {
        onChange({
            ...value,
            stops: value.stops.map(s => (s.id === id ? { ...s, ...patch } : s)),
        });
    };

    const addStop = () => {
        if (value.stops.length >= 5) return;
        const id = createStopId();
        const last = value.stops[value.stops.length - 1];
        onChange({
            ...value,
            stops: [
                ...value.stops,
                {
                    id,
                    color: last?.color || "#cfe8b8",
                    position: clamp((last?.position ?? 50) + 10, 0, 100),
                },
            ],
        });
        setActiveStopId(id);
    };

    const removeStop = (id: string) => {
        if (value.stops.length <= 2) return;
        const next = value.stops.filter(s => s.id !== id);
        onChange({ ...value, stops: next });
        if (activeStopId === id) setActiveStopId(next[0]?.id);
    };

    return (
        <div className={styles.gradientPanel}>
            <div className={styles.gradientHeader}>
                <SegmentControl
                    aria-label="Gradient type"
                    items={[...KIND_ITEMS]}
                    onValueChange={id =>
                        onChange({ ...value, kind: id as GradientKind })
                    }
                    value={value.kind}
                />

                <label className={styles.hexField}>
                    <span className={styles.fieldCaption}>CSS GRADIENT</span>
                    <input
                        aria-invalid={cssError || undefined}
                        className={cx(
                            styles.textInput,
                            cssError && styles.textInputError,
                        )}
                        onBlur={commitCssDraft}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setCssDraft(e.target.value);
                            setCssError(false);
                        }}
                        onKeyDown={e => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                commitCssDraft();
                                (e.target as HTMLInputElement).blur();
                            }
                        }}
                        spellCheck={false}
                        title="Edit CSS gradient (Enter or blur to apply)"
                        value={cssDraft}
                    />
                    {cssError ? (
                        <span className={styles.fieldError}>
                            Invalid gradient. Use linear/radial/conic with hex
                            stops.
                        </span>
                    ) : null}
                </label>
            </div>

            <div className={styles.gradientBody}>
                {(value.kind === "linear" || value.kind === "conic") && (
                    <label className={styles.angleRow}>
                        <span className={styles.fieldCaption}>
                            {value.kind === "conic" ? "From angle" : "Angle"}
                        </span>
                        <span className={styles.angleInputWrap}>
                            <input
                                className={styles.angleInput}
                                max={360}
                                min={0}
                                onChange={(
                                    e: ChangeEvent<HTMLInputElement>,
                                ) => {
                                    const n = Number(e.target.value);
                                    if (Number.isFinite(n)) {
                                        onChange({
                                            ...value,
                                            angle: clamp(n, 0, 360),
                                        });
                                    }
                                }}
                                type="number"
                                value={value.angle ?? 135}
                            />
                            <span className={styles.angleUnit}>deg</span>
                        </span>
                    </label>
                )}

                <ul className={styles.stopList}>
                    {value.stops.map((stop, index) => {
                        const selected = stop.id === activeStop?.id;
                        return (
                            <li
                                className={
                                    selected
                                        ? styles.stopRowSelected
                                        : styles.stopRow
                                }
                                key={stop.id}
                            >
                                <button
                                    aria-label={`Edit stop ${index + 1} color`}
                                    className={styles.stopSwatchBtn}
                                    onClick={() => setActiveStopId(stop.id)}
                                    style={{ background: stop.color }}
                                    type="button"
                                />
                                <input
                                    aria-label={`Stop ${index + 1} position`}
                                    className={styles.stopPos}
                                    max={100}
                                    min={0}
                                    onChange={(
                                        e: ChangeEvent<HTMLInputElement>,
                                    ) => {
                                        const n = Number(e.target.value);
                                        if (Number.isFinite(n)) {
                                            updateStop(stop.id, {
                                                position: clamp(n, 0, 100),
                                            });
                                        }
                                    }}
                                    type="number"
                                    value={stop.position}
                                />
                                <span className={styles.angleUnit}>%</span>
                                <button
                                    aria-label={`Remove stop ${index + 1}`}
                                    className={styles.stopRemove}
                                    disabled={value.stops.length <= 2}
                                    onClick={() => removeStop(stop.id)}
                                    type="button"
                                >
                                    ×
                                </button>
                            </li>
                        );
                    })}
                </ul>

                <button
                    className={styles.addStop}
                    disabled={value.stops.length >= 5}
                    onClick={addStop}
                    type="button"
                >
                    + Add color stop
                </button>

                {activeStop ? (
                    <div className={styles.stopEditor}>
                        <p className={styles.stopEditorLabel}>Stop color</p>
                        <SolidPanel
                            hex={activeStop.color}
                            onHexChange={hex =>
                                updateStop(activeStop.id, { color: hex })
                            }
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
}

/**
 * Treez DS — ColorField
 *
 * Trigger row + editor for solid / gradient colors.
 * `variant="popover"` (default) uses Radix Popover; `variant="accordion"`
 * expands the editor inline under the trigger.
 */
export function ColorField({
    label,
    value,
    onValueChange,
    disabled = false,
    description,
    allowedModes = ["solid", "gradient"],
    variant = "popover",
    open: openProp,
    defaultOpen = false,
    onOpenChange,
    className,
}: ColorFieldProps) {
    const labelId = useId();
    const isControlled = openProp !== undefined;
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const open = isControlled ? Boolean(openProp) : uncontrolledOpen;

    const setOpen = (next: boolean) => {
        if (disabled) return;
        if (!isControlled) setUncontrolledOpen(next);
        onOpenChange?.(next);
    };

    const modes = useMemo(
        () => MODE_ITEMS.filter(m => allowedModes.includes(m.id)),
        [allowedModes],
    );

    const effectiveValue = useMemo(() => {
        if (allowedModes.includes(value.mode)) return value;
        if (allowedModes.includes("solid")) {
            return defaultSolid(solidFromValue(value));
        }
        if (allowedModes.includes("gradient")) {
            return seedGradientFromSolid(solidFromValue(value));
        }
        return value;
    }, [allowedModes, value]);

    const css = colorValueToCss(effectiveValue);
    const summary = colorValueToLabel(effectiveValue);

    const setMode = (mode: ColorFieldMode) => {
        if (mode === effectiveValue.mode) return;
        if (mode === "solid") {
            onValueChange(defaultSolid(solidFromValue(effectiveValue)));
            return;
        }
        onValueChange(seedGradientFromSolid(solidFromValue(effectiveValue)));
    };

    const editor = (
        <>
            {modes.length > 1 ? (
                <SegmentControl
                    aria-label="Color mode"
                    items={[...modes]}
                    onValueChange={id => setMode(id as ColorFieldMode)}
                    value={effectiveValue.mode}
                />
            ) : null}

            {effectiveValue.mode === "solid" ? (
                <SolidPanel
                    hex={effectiveValue.hex}
                    onHexChange={hex => onValueChange({ mode: "solid", hex })}
                />
            ) : (
                <GradientPanel
                    onChange={onValueChange}
                    value={
                        effectiveValue.mode === "gradient"
                            ? effectiveValue
                            : defaultGradient()
                    }
                />
            )}
        </>
    );

    const trigger = (
        <button
            aria-expanded={open}
            aria-labelledby={labelId}
            className={styles.trigger}
            disabled={disabled}
            onClick={() => {
                if (variant === "accordion") setOpen(!open);
            }}
            type="button"
        >
            <span className={styles.triggerText}>
                <span className={styles.triggerLabel} id={labelId}>
                    {label}
                </span>
                <span className={styles.triggerValue}>{summary}</span>
                {description ? (
                    <span className={styles.triggerDesc}>{description}</span>
                ) : null}
            </span>
            <Swatch className={styles.triggerSwatch} css={css} />
        </button>
    );

    if (variant === "accordion") {
        return (
            <div
                className={cx(styles.root, className)}
                data-disabled={disabled || undefined}
                data-open={open || undefined}
                data-ui="color-field"
                data-variant="accordion"
            >
                {trigger}
                {open ? (
                    <div
                        className={styles.accordionPanel}
                        data-ui="color-field-accordion"
                    >
                        {editor}
                    </div>
                ) : null}
            </div>
        );
    }

    return (
        <div
            className={cx(styles.root, className)}
            data-disabled={disabled || undefined}
            data-ui="color-field"
            data-variant="popover"
        >
            <Popover.Root
                modal={false}
                onOpenChange={next => setOpen(next)}
                open={open}
            >
                <Popover.Trigger asChild>
                    <button
                        aria-labelledby={labelId}
                        className={styles.trigger}
                        disabled={disabled}
                        type="button"
                    >
                        <span className={styles.triggerText}>
                            <span className={styles.triggerLabel} id={labelId}>
                                {label}
                            </span>
                            <span className={styles.triggerValue}>
                                {summary}
                            </span>
                            {description ? (
                                <span className={styles.triggerDesc}>
                                    {description}
                                </span>
                            ) : null}
                        </span>
                        <Swatch className={styles.triggerSwatch} css={css} />
                    </button>
                </Popover.Trigger>

                <Popover.Portal>
                    <Popover.Content
                        align="center"
                        className={cx(
                            styles.popover,
                            effectiveValue.mode === "gradient" &&
                                styles.popoverGradient,
                        )}
                        data-ui="color-field-popover"
                        sideOffset={8}
                    >
                        {editor}
                    </Popover.Content>
                </Popover.Portal>
            </Popover.Root>
        </div>
    );
}

ColorField.displayName = "ColorField";

export {
    colorValueToCss,
    colorValueToLabel,
    colorValueToThemePair,
    defaultGradient,
    defaultSolid,
    normalizeHex,
    parseCssGradient,
    solidFromValue,
} from "./colorValue";
export type {
    ColorStop,
    ColorValue,
    GradientColorValue,
    GradientKind,
    SolidColorValue,
} from "./colorValue";
